'use client';

import { c as _c } from "react/compiler-runtime";
import type { GroupedCategory } from '@repo/types/maps/map-types';
import { useEffect, useState } from 'react';
const GAP_PX = 8;
const PILL_AREA_MARGIN_PX = 60;
const FIXED_GAP_COUNT = 3;
interface UseResponsivePillsParams {
  toolbarRef: React.RefObject<HTMLDivElement | null>;
  isMobileViewport: boolean;
  displayedCategories: GroupedCategory[];
  activeCategoryId: string | null;
}

/**
 * Priority+ pattern: measures fixed toolbar elements, then walks collapsible
 * pills left-to-right until space runs out. Overflow pills are hidden
 * imperatively in the same synchronous frame to avoid ResizeObserver thrash.
 */
export function useResponsivePills(t0) {
  const $ = _c(6);
  const {
    toolbarRef,
    isMobileViewport,
    displayedCategories,
    activeCategoryId
  } = t0;
  const [visibleCount, setVisibleCount] = useState(0);
  let t1;
  let t2;
  if ($[0] !== activeCategoryId || $[1] !== displayedCategories || $[2] !== isMobileViewport || $[3] !== toolbarRef) {
    t1 = () => {
      if (isMobileViewport) {
        return;
      }
      const toolbar = toolbarRef.current;
      if (!toolbar) {
        return;
      }
      const recalc = () => {
        if (activeCategoryId) {
          setVisibleCount(displayedCategories.length);
          return;
        }
        const pills = toolbar.querySelectorAll("[data-category-pill]");
        pills.forEach(_temp);
        const expandEl = toolbar.querySelector("[data-toolbar-expand]") as HTMLElement | null;
        const searchEl = toolbar.querySelector("[data-toolbar-search]") as HTMLElement | null;
        const moreEl = toolbar.querySelector("[data-toolbar-more]") as HTMLElement | null;
        const containerWidth = toolbar.clientWidth;
        const fixedWidth = (expandEl?.offsetWidth ?? 0) + (searchEl?.offsetWidth ?? 0) + (moreEl?.offsetWidth ?? 0) + PILL_AREA_MARGIN_PX;
        const available = containerWidth - fixedWidth - FIXED_GAP_COUNT * GAP_PX;
        let used = 0;
        let count = 0;
        const pillArray = Array.from(pills);
        for (let i = 0; i < pillArray.length; i++) {
          const btn_0 = (pillArray[i] as HTMLElement).firstElementChild as HTMLElement | null;
          if (!btn_0) {
            continue;
          }
          const needed = used === 0 ? btn_0.offsetWidth : btn_0.offsetWidth + GAP_PX;
          if (used + needed > available) {
            break;
          }
          used = used + needed;
          count++;
        }
        for (let i_0 = 0; i_0 < pillArray.length; i_0++) {
          const btn_1 = (pillArray[i_0] as HTMLElement).firstElementChild as HTMLElement | null;
          if (!btn_1) {
            continue;
          }
          btn_1.style.display = i_0 < count ? "" : "none";
        }
        setVisibleCount(count);
      };
      requestAnimationFrame(recalc);
      const observer = new ResizeObserver(recalc);
      observer.observe(toolbar);
      return () => observer.disconnect();
    };
    t2 = [isMobileViewport, displayedCategories, activeCategoryId, toolbarRef];
    $[0] = activeCategoryId;
    $[1] = displayedCategories;
    $[2] = isMobileViewport;
    $[3] = toolbarRef;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return visibleCount;
}
function _temp(span) {
  const btn = (span as HTMLElement).firstElementChild as HTMLElement | null;
  if (btn) {
    btn.style.display = "";
  }
}